import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { uploadImage, resolveImageUrl } from '../../lib/imageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSettings = () => {
  const [settings, setSettings] = useState({
    gcash_qr: '',
    bank_qr: '',
    bank_details: '',
    gcash_details: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings`);
      setSettings({ gcash_details: '', ...response.data });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleField = (key, value) => setSettings((p) => ({ ...p, [key]: value }));

  const handleImage = async (key, file) => {
    if (!file) return;
    try {
      const url = await uploadImage(file);
      handleField(key, url);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/payment-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Payment settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const QrField = ({ label, fieldKey, testid }) => (
    <div>
      <Label>{label} *</Label>
      <p className="text-xs text-zinc-500 mb-2">Upload an image of your QR code, or paste an image URL.</p>
      <div className="flex gap-2">
        <Input
          value={settings[fieldKey]?.startsWith('data:') ? '' : (settings[fieldKey] || '')}
          onChange={(e) => handleField(fieldKey, e.target.value)}
          placeholder="https://example.com/qr.png"
          data-testid={`${testid}-input`}
        />
        <label
          htmlFor={`${testid}-file`}
          className="flex items-center gap-2 cursor-pointer bg-zinc-900 text-white rounded px-4 text-sm font-medium whitespace-nowrap"
          data-testid={`${testid}-upload-label`}
        >
          <Upload size={16} /> Upload
          <input
            id={`${testid}-file`}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleImage(fieldKey, e.target.files?.[0])}
            data-testid={`${testid}-upload-input`}
          />
        </label>
      </div>
      {settings[fieldKey] && (
        <div className="relative inline-block mt-4" data-testid={`${testid}-preview`}>
          <img src={resolveImageUrl(settings[fieldKey])} alt={`${label} Preview`} className="w-48 border border-zinc-200" />
          <button
            type="button"
            onClick={() => handleField(fieldKey, '')}
            className="absolute -top-3 -right-3 bg-black text-white p-1.5 rounded-full hover:bg-zinc-700"
            data-testid={`${testid}-remove-btn`}
          >
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-8" data-testid="payment-settings">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--heading-font)' }}>
          Payment Settings
        </h2>
        <p className="text-zinc-600">Configure your payment QR codes, GCash details, and bank transfer details</p>
      </div>

      <div className="bg-white border border-zinc-200 p-8 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          <QrField label="GCash QR Code" fieldKey="gcash_qr" testid="gcash-qr" />

          <div>
            <Label htmlFor="gcash_details">GCash Details</Label>
            <Textarea
              id="gcash_details"
              value={settings.gcash_details || ''}
              onChange={(e) => handleField('gcash_details', e.target.value)}
              rows={4}
              placeholder={'GCash Name: STUDIO AT THE THIRD\nGCash Number: 0917 000 0000'}
              data-testid="gcash-details-input"
            />
            <p className="text-sm text-zinc-500 mt-2">
              Shown to customers under the GCash QR code (e.g. account name and number).
            </p>
          </div>

          <QrField label="Bank / QR PH QR Code" fieldKey="bank_qr" testid="bank-qr" />

          <div>
            <Label htmlFor="bank_details">Bank Transfer Details *</Label>
            <Textarea
              id="bank_details"
              value={settings.bank_details || ''}
              onChange={(e) => handleField('bank_details', e.target.value)}
              rows={6}
              placeholder={'Bank Name:\nAccount Name: STUDIO AT THE THIRD\nAccount Number:'}
              required
              data-testid="bank-details-input"
            />
            <p className="text-sm text-zinc-500 mt-2">
              Shown to customers when they select bank transfer.
            </p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-8 py-4 text-lg font-bold transition-transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="save-settings-btn"
          >
            <Save size={20} />
            {saving ? 'SAVING...' : 'SAVE SETTINGS'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PaymentSettings;
